<template>
  <TooltipWrapper top>
    <template #content>
      <div class="text-label-m text-subtle">
        {{ isDetailBudget ? t('Reviews.product.campaign.campaignBudget.tooltip') : t('Reviews.product.campaign.globalBudget.tooltip') }}
      </div>
    </template>
    <template #trigger>
      <div class="h-full rounded-sm bg-surface-elevation-1 p-4 pb-6 shadow-sm">
        <div class="mb-7 flex items-center justify-between">
          <div v-if="title" class="text-headline-xs text-subtle">
            {{ title }}
          </div>
          <div v-if="budgetReachedOrExceeded">
            <ChipBadge color="default" rounded>{{ t(`Reviews.product.campaign.globalBudget.${budgetReachedOrExceeded}`) }}</ChipBadge>
          </div>
        </div>
        <div>
          <span
            v-if="totalBudget && !hasReviewsLimit"
            class="mr-1 inline-block h-2 w-2 rounded-full"
            :class="budgetReachedOrExceeded ? 'bg-error-strong' : 'bg-primary-strong'"
          />
          <span class="text-label-m text-subtlest">
            {{ isDetailBudget ? t('Reviews.product.campaign.detailBudget.usedHint') : t('Reviews.product.campaign.globalBudget.usedHint') }}
          </span>
        </div>
        <div class="mb-2 text-body-s text-subtlest">
          <span class="text-headline-s text-subtle">{{ used }}</span>
          <span v-if="!!totalBudget">/{{ total }} </span>
          <span v-if="hasReviewsLimit" class="text-label-m text-subtlest"> ({{ progress }}%) </span>
        </div>
        <LinearProgress
          v-if="totalBudget && !hasReviewsLimit"
          class="mb-6"
          percentage="outside"
          :progress="progress"
          overshoot-style="error"
          :overshoot-threshold="80"
        />
        <div class="flex items-center text-label-m text-subtlest" :class="hasReviewsLimit ? 'mt-16' : ''">
          <InfoIcon class="mr-1 lucide-icon-xs shrink-0" />
          <span v-if="endDate" v-html="t('Reviews.product.campaign.globalBudget.expireHint', { expireAt: endDate.toLocaleDateString() })" />
          <span v-else class="py-3">
            {{ t('Reviews.product.campaign.globalBudget.remainingBudgetHint', { globalBudget: globalBudget }) }}</span
          >
        </div>
      </div>
    </template>
  </TooltipWrapper>
</template>
<script setup lang="ts">
import { ChipBadge, LinearProgress, TooltipWrapper, useCurrencyFormatter } from '@ramp106/omrjs-core-ui'
import { InfoIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  title?: string
  currency?: string
  totalBudget: number | null
  usedBudget: number
  endDate?: Date
  isDetailBudget?: boolean
  globalBudget?: number
  hasReviewsLimit?: boolean
}>()

const { t } = useI18n()
const { formatCurrency } = useCurrencyFormatter()

const total = computed(() => formatCurrency(props.totalBudget ?? 0, { currency: props.currency }))
const used = computed(() => formatCurrency(props.usedBudget, { currency: props.currency }))
const progress = computed(() => {
  if (props.totalBudget === null) {
    return 0
  }
  return Math.round((props.usedBudget / props.totalBudget) * 100)
})
const globalBudget = computed(() => formatCurrency(props.globalBudget ?? 0, { currency: props.currency }))
const budgetReachedOrExceeded = computed(() => {
  if (props.totalBudget === null) {
    return false
  }
  if (props.usedBudget > props.totalBudget) {
    return 'exceeded'
  }
  if (props.usedBudget === props.totalBudget) {
    return 'reached'
  }
  if (props.usedBudget >= props.totalBudget - props.totalBudget * 0.2) {
    return 'almostReached'
  }
  return false
})
</script>
